import { useNavigation } from '@react-navigation/native';
import { useStoreActions, useStoreState } from '@/stores/store';
import { useCallback, useRef, useState } from 'react';
import { Platform, StyleSheet, View } from 'react-native';
import { Text } from '@/components/Text';
import { useNotification } from '@/components/ui/therapist/Notification/NotificationProvider';
import { fetchPatient } from '../../api/services/patient';
import colors, { appTheme } from '@/config/colors';
import encryptedStorage from '../../stores/encryptedStorage';
import { hasInternetAsync } from './helpers';
import { Icon } from '@/components/Icon';
import { logError } from '@/utils/logHandling';
import { Button } from '@/components/ui/therapist/Button';
import { Modal } from '@/components/ui/therapist/Modal';
import { TherapistStackParamList } from '@/navigations/TherapistStack';
import { StackNavigationProp } from '@react-navigation/stack';

const storage = encryptedStorage();

const styles = StyleSheet.create({
  buttonContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
  },
  buttonContainerInner: { flexDirection: 'row', gap: 16 },
  infoContainer: { gap: 8 },
  infoContainerInner: { flexDirection: 'row', alignItems: 'center' },
  icon: {
    fontSize: 24,
    marginRight: 4,
  },
});

export function DashboardSettings({
  selectedID,
  code,
  activeWordsets,
  activeSentencesets,
  activeTextsets,
  selectedPatient,
}) {
  const { isTherapistDemo } = useStoreState((store) => store);
  const [activeModal, setActiveModal] = useState(false);
  const [downloadModal, setDownloadModal] = useState(false);
  const [noCodeText, setNoCodeText] = useState('kein Patienten-Code verbunden');
  const [noSetText, setNoSetText] = useState(
    'kein aktives Übungsset zum Eigentraining gewählt',
  );

  const { showNotification } = useNotification();

  const activeSetsRef = useRef([]);
  const activeSetsObjRef = useRef([]);

  const permission = useStoreState((state) => state.permissions);
  const { triggerRefresh } = useStoreActions((actions) => ({
    triggerRefresh: actions.triggerRefresh,
  }));
  const { navigate } =
    useNavigation<StackNavigationProp<TherapistStackParamList>>();

  const LENGTHOF =
    activeWordsets?.length === 0 &&
    activeSentencesets?.length === 0 &&
    activeTextsets?.length === 0;

  const { setGlobalPopupState } = useStoreActions((actions) => ({
    setGlobalPopupState: actions.setGlobalPopupState,
  }));

  const handleSettingsPress = useCallback(async (selectedID) => {
    if (isTherapistDemo) {
      showNotification({
        type: 'error',
        title: 'In der Demo nicht möglich',
        position: 'top',
      });
    } else {
      try {
        await fetchPatient(selectedID);
        navigate('SelfTrainingSettings', { id: selectedID ?? '' });
      } catch (error) {
        logError('Could not fetch patient', error);
        setGlobalPopupState({
          headerText: 'Keine Verbindung zum neolexon Server',
          bodyText:
            'Überprüfen Sie Ihre Internetverbindung und versuchen es erneut.',
          leftButton: '',
          rightButton: '',
        });
      }
    }
  }, []);

  const handlePlayPress = useCallback(
    async (preview?: boolean) => {
      // check if there is an active wordset before starting eigentraining
      if (LENGTHOF) {
        setActiveModal(true);
      } else {
        try {
          const activeSets = [
            ...activeSentencesets,
            ...activeTextsets,
            ...activeWordsets,
          ];
          const activeSetsMapped = activeSets.map((setKey) =>
            storage.getItem(setKey),
          );
          const allActiveSets = await Promise.all(activeSetsMapped);
          const allSynced = allActiveSets.every((set) => set.sync);

          if (allSynced) {
            // all sets are set to sync, check if all wordsets are really downloaded. if not push to corruptSets
            const corruptSetIds = [];
            const corruptSets = [];
            allActiveSets.forEach(function (localWordset, index) {
              if (
                !localWordset.activeWordset ||
                !localWordset.preparedSet.exercises
              ) {
                delete localWordset.activeWordset;
                delete localWordset.assets;
                localWordset.preparedSet = {};
                localWordset.preparedSet.date = {};
                localWordset.sync = false;
                corruptSetIds.push(activeSets[index]);
                corruptSets.push(localWordset);
              }
            });

            // one or more active sets are corrupt. Delete sets from storage and display warning.
            if (corruptSetIds.length !== 0) {
              const corruptedSetsIdsMapped = corruptSetIds.map((id, index) => {
                return storage.setItem(id, corruptSets[index]);
              });
              await Promise.all(corruptedSetsIdsMapped);
              setDownloadModal(true);
            } else {
              // all active sets are downloaded on the tablet! Go to Eigentraining.
              // Navigate to self training selection
              navigate('SelfTraining', {
                activeSets,
                selectedPatient,
                preview: !!preview,
              });
            }
          } else {
            activeSetsRef.current = activeSets;
            activeSetsObjRef.current = allActiveSets;
            const hasInternet = await hasInternetAsync();
            if (!hasInternet) {
              setGlobalPopupState({
                headerText: 'Keine Verbindung zum neolexon Server',
                bodyText:
                  'Überprüfen Sie Ihre Internetverbindung und versuchen es erneut.',
                leftButton: '',
                rightButton: '',
              });
            } else {
              setDownloadModal(true);
            }
          }
        } catch (error) {
          logError('handlePlayPressErr', error);
        }
      }
    },
    [selectedID, LENGTHOF, activeWordsets, activeSentencesets, activeTextsets],
  );

  const closeActiveModal = useCallback(() => {
    setActiveModal(false);
  }, []);

  const closeDownloadModal = useCallback(() => {
    setDownloadModal(false);
  }, []);

  const triggerSetsDownload = useCallback(async () => {
    const activeSets = activeSetsRef.current;
    const localWordsets = activeSetsObjRef.current;
    localWordsets.forEach((localWordset) => {
      localWordset.sync = true;
    });
    const activeSetsPromise = activeSets.map((set, id) =>
      storage.setItem(set, localWordsets[id]),
    );
    await Promise.all(activeSetsPromise);
    triggerRefresh();
    closeDownloadModal();
  }, []);

  const handleTextLayoutCode = useCallback((event) => {
    const { lines } = event.nativeEvent;
    if (lines.length == 1) {
      return;
    }
    setNoCodeText('kein Patienten-Code');
  }, []);

  const handleTextLayoutSet = useCallback((event) => {
    const { lines } = event.nativeEvent;
    if (lines.length == 1) {
      return;
    }
    setNoSetText('kein aktives Übungsset');
  }, []);

  // Only for web: if the user logs in with this params, he can only change the settings
  let canStartTraining = true;
  if (Platform.OS === 'web') {
    const currentUrlParams = new URLSearchParams(window.location.search);
    canStartTraining =
      currentUrlParams.get('nur-eigentraining-einstellen') === null;
  }

  return (
    <View>
      {/* No sets active modal */}
      <Modal
        isVisible={activeModal}
        onClose={closeActiveModal}
        confirmActionButton={
          <Button label="Schließen" onPress={closeActiveModal} />
        }
        title="Kein aktives Übungsset gewählt"
      >
        <Text style={{ fontSize: 20 }}>
          Bitte wählen Sie ein aktives Übungsset, um das Eigentraining
          durchzuführen.
        </Text>
      </Modal>
      {/* Download set modal */}
      <Modal
        isVisible={downloadModal}
        title="Herunterladen erforderlich."
        confirmActionButton={
          <Button label="Herunterladen" onPress={triggerSetsDownload} />
        }
        cancelActionButton={
          <Button
            label="Abbrechen"
            look="secondary"
            onPress={closeDownloadModal}
          />
        }
        onClose={closeDownloadModal}
      >
        <Text style={{ fontSize: 20 }}>
          {permission?.eigentraining && canStartTraining
            ? 'Zum Durchführen des Eigentrainings ist das Herunterladen der aktiven Übungssets nötig.'
            : 'Zur Vorschau des Eigentrainings ist das Herunterladen der aktiven Übungssets nötig.'}
        </Text>
      </Modal>
      <View style={styles.buttonContainer}>
        <View style={styles.buttonContainerInner}>
          <Button
            label="Einstellungen"
            icon="settings"
            onPress={() => handleSettingsPress(selectedID)}
          />
          {permission?.eigentraining && canStartTraining ? (
            <Button
              label="Eigentraining starten"
              icon="playCircle"
              onPress={() => handlePlayPress(false)}
            />
          ) : (
            <Button
              label="Eigentraining Vorschau"
              icon="playCircle"
              onPress={() => handlePlayPress(true)}
            />
          )}
        </View>
        <View style={styles.infoContainer}>
          <View style={styles.infoContainerInner}>
            {code ? (
              <>
                <Icon
                  name="checkCircle"
                  style={{
                    ...styles.icon,
                    color: appTheme.primary,
                  }}
                />
                <Text>Patienten-Code verbunden</Text>
              </>
            ) : (
              <>
                <Icon
                  name="alertCircle"
                  style={{
                    ...styles.icon,
                    color: colors.red,
                  }}
                />
                <Text onTextLayout={handleTextLayoutCode}>{noCodeText}</Text>
              </>
            )}
          </View>
          <View style={styles.infoContainerInner}>
            {!LENGTHOF ? (
              <>
                <Icon
                  name="checkCircle"
                  style={{
                    ...styles.icon,
                    color: appTheme.primary,
                  }}
                />
                <Text>aktives Übungsset gewählt</Text>
              </>
            ) : (
              <>
                <Icon
                  name="alertCircle"
                  style={{
                    ...styles.icon,
                    color: colors.red,
                  }}
                />
                <Text onTextLayout={handleTextLayoutSet}>{noSetText}</Text>
              </>
            )}
          </View>
        </View>
      </View>
    </View>
  );
}
